import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatPHP, formatErrorDetail } from "@/lib/api";
import { BriefcaseBusiness, MapPin, Building2, Loader2, ArrowRight, Share2, Check } from "lucide-react";

const API_BASE = api.defaults.baseURL || "/api";

export async function copyLink(url) {
    // Clipboard API first; textarea+execCommand fallback for browsers/webviews that deny it.
    try { await navigator.clipboard.writeText(url); return true; } catch { /* fall through */ }
    try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) return true;
    } catch { /* fall through */ }
    window.prompt("Copy this job link:", url);
    return false;
}

function ShareButton({ job, companyName }) {
    // Each posting has its own public URL — this button hands it out for social sharing.
    const [copied, setCopied] = useState(false);
    const share = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const url = `${window.location.origin}/careers/${job.public_token}`;
        const title = `${job.title}${companyName ? ` at ${companyName}` : ""}`;
        if (navigator.share) {
            try { await navigator.share({ title, text: `We're hiring: ${title}`, url }); return; } catch { /* fall through to copy */ }
        }
        if (await copyLink(url)) {
            setCopied(true);
            toast.success("Job link copied — ready to share");
            setTimeout(() => setCopied(false), 2500);
        }
    };
    return (
        <button
            type="button"
            onClick={share}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-700 bg-white"
            data-testid={`board-share-${job.public_token}`}
        >
            {copied ? <Check className="size-3.5 text-emerald-600" /> : <Share2 className="size-3.5" />} {copied ? "Copied" : "Share"}
        </button>
    );
}

export default function PublicCareers() {
    const { companyToken } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/public/companies/${companyToken}/jobs`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(formatErrorDetail(e.response?.data?.detail) || "Careers page not available."));
    }, [companyToken]);

    if (err) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
                <div className="max-w-md text-center">
                    <BriefcaseBusiness className="size-10 mx-auto text-slate-300 mb-3" />
                    <h1 className="text-lg font-semibold text-slate-800">Careers page unavailable</h1>
                    <p className="text-sm text-slate-500 mt-1">{err}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm"><Loader2 className="size-4 animate-spin mr-2" /> Loading…</div>;
    }

    const jobs = data.jobs || [];

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                    {data.company_name && <span className="inline-flex items-center gap-1"><Building2 className="size-3.5" /> {data.company_name}</span>}
                </div>
                <h1 className="text-3xl font-bold text-slate-900">Open positions</h1>
                <p className="text-sm text-slate-500 mt-1">
                    {jobs.length ? `${jobs.length} open role${jobs.length > 1 ? "s" : ""} — click a role to view details and apply.` : "There are no open positions right now. Please check back soon."}
                </p>

                <div className="mt-6 space-y-3">
                    {jobs.map((j) => (
                        <Link
                            key={j.public_token}
                            to={`/careers/${j.public_token}`}
                            className="block bg-white rounded-2xl border shadow-sm overflow-hidden hover:shadow-md hover:border-indigo-300 transition group"
                            data-testid={`board-job-${j.public_token}`}
                        >
                            {j.has_photo && (
                                <img
                                    src={`${API_BASE}/public/jobs/${j.public_token}/photo`}
                                    alt={j.title}
                                    className="w-full h-44 object-cover"
                                    data-testid={`board-photo-${j.public_token}`}
                                />
                            )}
                            <div className="p-5">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <h2 className="text-lg font-semibold text-slate-900 group-hover:text-indigo-700">{j.title}</h2>
                                        <div className="mt-1 flex flex-wrap gap-3 text-sm text-slate-600">
                                            {j.company_name && j.company_name !== data.company_name && <span className="inline-flex items-center gap-1 font-medium text-slate-800"><Building2 className="size-4" /> {j.company_name}</span>}
                                            {j.department && <span className="inline-flex items-center gap-1"><BriefcaseBusiness className="size-4" /> {j.department}</span>}
                                            {j.location && <span className="inline-flex items-center gap-1"><MapPin className="size-4" /> {j.location}</span>}
                                            {j.employment_type && <span className="capitalize">{String(j.employment_type).replaceAll("_", " ")}</span>}
                                            {j.remote_type && <span>· {({ onsite: "On-site", hybrid: "Hybrid", remote: "Remote" })[j.remote_type] || j.remote_type}</span>}
                                        </div>
                                        {(j.salary_min || j.salary_max) && (
                                            <div className="mt-1 text-sm font-medium text-emerald-700">
                                                {formatPHP(j.salary_min)}{j.salary_max ? ` – ${formatPHP(j.salary_max)}` : ""} / month
                                            </div>
                                        )}
                                        {!!(j.required_skills?.length) && (
                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                {j.required_skills.slice(0, 6).map((s) => <span key={s} className="text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-700">{s}</span>)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-2 shrink-0 mt-1">
                                        <ArrowRight className="size-5 text-slate-300 group-hover:text-indigo-500" />
                                        <ShareButton job={j} companyName={data.company_name} />
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                <p className="mt-8 text-[11px] text-center text-slate-400">Powered by SAHOD HRIS</p>
            </div>
        </div>
    );
}
